import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { formatCurrency, getGreeting, formatDate, formatDateWithTime, getCurrencySymbol, localizeCategory } from '../utils/format';
import { hapticFeedback } from '../utils/telegram';
import { AddTransactionFlow, TransactionData } from '../components/AddTransactionFlow';
import { Category } from '../types';
import QuickActions from '../components/QuickActions';
import '../components/QuickActions/QuickActions.css';
import { HomeTabs, SummaryCards, InteractivePieChart, type HomeTabType } from '../components/home';
import QuickTransactionFlow from '../components/QuickTransactionFlow';
import { TransactionEditModal } from '../components/TransactionEditModal';
import { QuickAddTransaction } from '../components/QuickAddTransaction';
import { Preloader } from '../components/Preloader';
import { useTranslation } from 'react-i18next';
import './HomeScreen.css';
import { usePremiumFeature } from '../hooks/usePremiumFeature';
import { PremiumUpgradeModal } from '../components/PremiumFeature/PremiumUpgradeModal';
import { DemoVoiceModal } from '../components/DemoVoiceModal';
import { DemoReceiptModal } from '../components/DemoReceiptModal';
import { trackAddTransaction, trackEditTransaction, trackDeleteTransaction, trackError as trackAnalyticsError } from '../services/analytics';
import { useWebPowerSyncTransactions, useWebPowerSyncCategories } from '../hooks/usePowerSync';
import { AnalyticsScreen } from './AnalyticsScreen';
import { RecurringTransactionsScreen } from './RecurringTransactionsScreen';
import { CategoriesContent } from './CategoriesScreen';
import { LimitsScreen } from './LimitsScreen';

interface FrequentCategory {
  category: string;
  icon: string;
  count: number;
  avg_amount: number;
  frequent_titles: string[];
}

export const HomeScreen: React.FC = () => {
  const { transactions: storeTransactions, setTransactions, categories: storeCategories, setCategories, isLoading, setLoading, setCurrentPage, settings, setSettings } = useAppStore();
  const { t } = useTranslation();
  
  // PowerSync данные (реактивные, обновляются автоматически при синхронизации)
  const { transactions: psTransactions, isLoading: psTransLoading } = useWebPowerSyncTransactions();
  const { categories: psCategories, isLoading: psCatLoading } = useWebPowerSyncCategories();
  
  // Используем PowerSync данные если доступны, иначе fallback на store
  const transactions = psTransactions.length > 0 ? psTransactions : storeTransactions;
  const categories = psCategories.length > 0 ? psCategories.map((c: any) => ({
    id: c.name || c.id,
    name: c.name,
    emoji: c.icon || c.emoji || null,
    is_system: c.is_system,
    type: 'both' as const,
  })) : storeCategories;
  
  // Premium проверки для функций
  const voicePremium = usePremiumFeature('voice_input');
  const receiptPremium = usePremiumFeature('receipt_scanner');
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expense: 0,
    balance: 0,
    total_current_balance: 0,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddFlow, setShowAddFlow] = useState(false);
  const [showTransactionTypeModal, setShowTransactionTypeModal] = useState(false);
  const [transactionType, setTransactionType] = useState<'income' | 'expense'>('expense');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddType, setQuickAddType] = useState<'income' | 'expense'>('expense');
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [transcribedText, setTranscribedText] = useState<string>('');
  const [dataError, setDataError] = useState<string | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voiceTransactionStatus, setVoiceTransactionStatus] = useState<string>('');
  const [voiceParsedData, setVoiceParsedData] = useState<any>(null); // Данные от голосового распознавания
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [receiptImage, setReceiptImage] = useState<File | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  
  // Premium модалки
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showDemoVoiceModal, setShowDemoVoiceModal] = useState(false);
  const [showDemoReceiptModal, setShowDemoReceiptModal] = useState(false);
  const [premiumFeatureRequested, setPremiumFeatureRequested] = useState<'voice_input' | 'receipt_scanner' | null>(null);
  
  // Очередь распознанных голосом транзакций
  const [voiceQueueResults, setVoiceQueueResults] = useState<any[]>([]);
  const [currentVoiceIndex, setCurrentVoiceIndex] = useState(0);
  const [processedVoiceCount, setProcessedVoiceCount] = useState(0);

  // Bulk receipt upload - ПАРАЛЛЕЛЬНАЯ ОБРАБОТКА
  const [receiptQueue, setReceiptQueue] = useState<File[]>([]);
  const [currentReceiptIndex, setCurrentReceiptIndex] = useState(0);
  const [processedReceipts, setProcessedReceipts] = useState<number>(0);
  const [receiptParsedData, setReceiptParsedData] = useState<any>(null); // Данные текущего чека для подтверждения
  const [showReceiptProgress, setShowReceiptProgress] = useState(false); // Показывать прогресс между чеками
  
  // Новые state для параллельной обработки
  const [processedReceiptsResults, setProcessedReceiptsResults] = useState<Array<{
    file: File;
    data: any;
    error?: string;
    index: number;
  }>>([]);
  const [totalReceiptsToProcess, setTotalReceiptsToProcess] = useState(0);
  const [scannedInBackground, setScannedInBackground] = useState(0); // Сколько чеков отсканировано в фоне
  
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  
  
  // Категории расходов для pie chart
  const [categoryExpenses, setCategoryExpenses] = useState<Array<{category: string; amount: number; percentage: number}>>([]);
  const [isLoadingPieChart, setIsLoadingPieChart] = useState(false);
  
  // Синхронизируем PowerSync данные в Zustand store
  useEffect(() => {
    if (psTransactions.length > 0) {
      setTransactions(psTransactions as any);
    }
  }, [psTransactions]);

  useEffect(() => {
    if (psCategories.length > 0) {
      setCategories(psCategories.map((c: any) => ({
        id: c.name || c.id,
        name: c.name,
        emoji: c.icon || c.emoji || null,
        is_system: c.is_system,
        type: 'both' as const,
      })));
    }
  }, [psCategories]);

  
  // Быстрые действия
  const [frequentCategories, setFrequentCategories] = useState<FrequentCategory[]>([]);
  const [selectedQuickCategory, setSelectedQuickCategory] = useState<FrequentCategory | null>(null);
  const [showQuickTransactionFlow, setShowQuickTransactionFlow] = useState(false);

  // HomeTabs: активный таб на главной
  const [homeTab, setHomeTab] = useState<HomeTabType>('home');

  // Функция для определения иконки по категории
  const getCategoryIcon = (categoryName: string): string => {
    const name = categoryName.toLowerCase();
    
    // Продукты и еда
    if (name.includes('продукт') || name.includes('еда') || name.includes('grocery')) return 'shopping-bag';
    if (name.includes('ресторан') || name.includes('кафе') || name.includes('restaurant') || name.includes('cafe')) return 'coffee';
    if (name.includes('напит') || name.includes('кофе') || name.includes('coffee')) return 'coffee';
    
    // Транспорт
    if (name.includes('транспорт') || name.includes('transport') || name.includes('taxi') || name.includes('такси')) return 'car';
    if (name.includes('бензин') || name.includes('заправ') || name.includes('fuel')) return 'zap';
    if (name.includes('парков') || name.includes('parking')) return 'car';
    
    // Развлечения
    if (name.includes('развлеч') || name.includes('entertainment') || name.includes('кино')) return 'play';
    if (name.includes('спорт') || name.includes('sport') || name.includes('фитнес')) return 'activity';
    if (name.includes('игр') || name.includes('game')) return 'gamepad';
    if (name.includes('музык') || name.includes('music') || name.includes('концерт')) return 'music';
    
    // Дом и коммунальные услуги
    if (name.includes('жилье') || name.includes('аренд') || name.includes('rent') || name.includes('квартир')) return 'home';
    if (name.includes('коммунал') || name.includes('utilities') || name.includes('свет') || name.includes('вода')) return 'zap';
    if (name.includes('ремонт') || name.includes('repair')) return 'construction';
    if (name.includes('мебел') || name.includes('furniture')) return 'home';
    
    // Здоровье
    if (name.includes('здоров') || name.includes('health') || name.includes('аптек') || name.includes('врач') || name.includes('лекарств')) return 'goal-health';
    if (name.includes('стомат') || name.includes('dental') || name.includes('зуб')) return 'goal-health';
    
    // Образование
    if (name.includes('образован') || name.includes('education') || name.includes('курс') || name.includes('обучен')) return 'goal-education';
    if (name.includes('книг') || name.includes('book')) return 'goal-education';
    
    // Одежда и покупки
    if (name.includes('одежд') || name.includes('clothes') || name.includes('обув')) return 'shopping-cart';
    if (name.includes('покупк') || name.includes('shopping') || name.includes('магазин')) return 'shopping-cart';
    
    // Связь и интернет
    if (name.includes('связь') || name.includes('телефон') || name.includes('интернет') || name.includes('phone')) return 'smartphone';
    if (name.includes('подписк') || name.includes('subscription')) return 'credit_card';
    
    // Подарки
    if (name.includes('подарок') || name.includes('gift') || name.includes('сувенир')) return 'gift';
    
    // Путешествия
    if (name.includes('путешеств') || name.includes('travel') || name.includes('отпуск') || name.includes('отдых')) return 'goal-travel';
    if (name.includes('отель') || name.includes('hotel') || name.includes('гостиниц')) return 'home';
    if (name.includes('самолет') || name.includes('plane') || name.includes('авиа')) return 'plane';
    
    // Красота
    if (name.includes('красот') || name.includes('beauty') || name.includes('салон') || name.includes('косметик')) return 'star';
    
    // Цели и накопления
    if (name.includes('цел') || name.includes('goal') || name.includes('накоплен')) return 'target';
    if (name.includes('копилк') || name.includes('savings')) return 'goal-piggy-bank';
    
    // Домашние животные
    if (name.includes('питом') || name.includes('pet') || name.includes('животн') || name.includes('собак') || name.includes('кошк')) return 'star';
    
    // Благотворительность
    if (name.includes('благотвор') || name.includes('charity') || name.includes('донат') || name.includes('помощ')) return 'gift';
    
    // Налоги и платежи
    if (name.includes('налог') || name.includes('tax') || name.includes('штраф') || name.includes('fine')) return 'file-text';
    
    // Разное по умолчанию
    return 'folder';
  };

  // Состояния для раздельной загрузки данных
  const [isLoadingCritical, setIsLoadingCritical] = useState(true);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingFrequent, setIsLoadingFrequent] = useState(false);

  useEffect(() => {
    // Проверяем наличие токена перед загрузкой данных
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.log('⚠️ No auth token, skipping data load');
      setIsLoadingCritical(false);
      return;
    }
    
    // Сначала загружаем критичные данные (настройки и баланс)
    loadCriticalData();
    
    // Затем загружаем некритичные данные в фоне
    // Используем setTimeout чтобы дать браузеру возможность отрисовать интерфейс
    setTimeout(() => {
      loadBackgroundData();
    }, 100);
  }, []);



  // Загрузка критичных данных (настройки и баланс) - блокирует первый рендер
  const loadCriticalData = async () => {
    try {
      setIsLoadingCritical(true);
      setDataError(null);
      console.log('📊 Loading critical data (settings + summary)...');
      
      // Загружаем настройки и сводку параллельно
      const [settingsData, summaryData] = await Promise.all([
        apiClient.getSettings(),
        apiClient.getSummary('month')
      ]);
      
      // Обрабатываем настройки
      const actualSettings = (settingsData as any).settings || settingsData;
      setSettings(actualSettings);
      
      // Обрабатываем сводку
      const currentBalance = summaryData.total_current_balance ?? 0;
      setSummary({
        total_income: summaryData.total_income || 0,
        total_expense: summaryData.total_expense || 0,
        balance: summaryData.balance || 0,
        total_current_balance: currentBalance,
      });
      
      console.log('✅ Critical data loaded');
    } catch (error: any) {
      console.error('❌ Error loading critical data:', error);
      setDataError(error.message || 'Failed to load data');
    } finally {
      setIsLoadingCritical(false);
    }
  };

  // Загрузка некритичных данных в фоне (не блокирует интерфейс)
  const loadBackgroundData = async () => {
    console.log('📊 Loading background data (transactions, categories, frequent)...');
    
    // Загружаем все некритичные данные параллельно
    await Promise.all([
      loadTransactions(),
      loadCategories(),
      loadFrequentTransactions()
    ]);
    
    console.log('✅ Background data loaded');
  };

  // Загрузка транзакций отдельно (пропускаем если PowerSync уже предоставил данные)
  const loadTransactions = async () => {
    if (psTransactions.length > 0) {
      console.log('💳 [PowerSync] Using PowerSync transactions, skipping API call');
      return;
    }
    try {
      setIsLoadingTransactions(true);
      console.log('💳 Fetching transactions from API (fallback)...');
      const transactionsData = await apiClient.getTransactions('month');
      setTransactions(transactionsData.transactions || []);
    } catch (error) {
      console.error('❌ Error loading transactions:', error);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Загрузка категорий отдельно (пропускаем если PowerSync уже предоставил данные)
  const loadCategories = async () => {
    if (psCategories.length > 0) {
      console.log('🏷️ [PowerSync] Using PowerSync categories, skipping API call');
      return;
    }
    try {
      setIsLoadingCategories(true);
      console.log('🏷️ Fetching all categories from API (fallback)...');
      const allCategoriesData = await apiClient.getAllCategories();
      
      const systemCategories = (allCategoriesData.system_categories || []).map(cat => ({
        id: cat.db_name,
        name: cat.db_name,
        emoji: cat.emoji || null,
        is_system: true,
        type: 'both' as const
      }));
      
      const userCategories = (allCategoriesData.user_categories || []).map(catName => ({
        id: catName,
        name: catName,
        emoji: null,
        is_system: false,
        type: 'both' as const
      }));
      
      const allCategories = [
        ...systemCategories.sort((a, b) => a.name.localeCompare(b.name)),
        ...userCategories.sort((a, b) => a.name.localeCompare(b.name))
      ];
      
      setCategories(allCategories);
    } catch (error: any) {
      console.error('❌ Failed to load categories:', error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Загрузка частых транзакций отдельно
  const loadFrequentTransactions = async () => {
    try {
      setIsLoadingFrequent(true);
      console.log('⚡ Fetching frequent transactions...');
      const frequentData = await apiClient.getFrequentTransactions(6);
      setFrequentCategories(frequentData.frequent_categories || []);
    } catch (error) {
      console.warn('⚠️ Failed to load frequent transactions:', error);
    } finally {
      setIsLoadingFrequent(false);
    }
  };

  // Старая функция loadData для совместимости (используется в других местах)
  const loadData = async () => {
    try {
      setLoading(true);
      setDataError(null);
      console.log('📊 Loading home screen data...');
      
      // Загружаем настройки пользователя
      console.log('⚙️ Fetching user settings...');
      const settingsData = await apiClient.getSettings();
      console.log('⚙️ Settings data:', settingsData);
      
      // API может возвращать {settings: {...}} или {...} напрямую
      const actualSettings = (settingsData as any).settings || settingsData;
      console.log('⚙️ Actual settings:', actualSettings);
      console.log('⚙️ subscription_status:', actualSettings.subscription_status);
      console.log('⚙️ currency_code:', actualSettings.currency_code);
      console.log('⚙️ currency:', actualSettings.currency);
      setSettings(actualSettings);
      
      // Загружаем сводку
      console.log('📈 Fetching summary...');
      const summaryData = await apiClient.getSummary('month');
      
      const currentBalance = summaryData.total_current_balance ?? 0;
      setSummary({
        total_income: summaryData.total_income || 0,
        total_expense: summaryData.total_expense || 0,
        balance: summaryData.balance || 0,
        total_current_balance: currentBalance,
      });
      
      // Загружаем последние транзакции
      console.log('💳 Fetching transactions...');
      const transactionsData = await apiClient.getTransactions('month');
      console.log('💳 Transactions data:', transactionsData);
      setTransactions(transactionsData.transactions || []);
      
      // Загружаем расходы по категориям для pie chart
      console.log('🥧 Fetching category expenses...');
      try {
        setIsLoadingPieChart(true);
        const categoryData = await apiClient.getCategoryExpenses('month');
        console.log('🥧 Category expenses data:', categoryData);
        setCategoryExpenses(categoryData.expenses || []);
      } catch (error) {
        console.error('🥧 Error loading category expenses:', error);
        setCategoryExpenses([]);
      } finally {
        setIsLoadingPieChart(false);
      }
      
      // Загружаем частые транзакции
      console.log('⚡ Fetching frequent transactions...');
      try {
        const frequentData = await apiClient.getFrequentTransactions(6);
        console.log('⚡ Frequent data:', frequentData);
        setFrequentCategories(frequentData.frequent_categories || []);
      } catch (error) {
        console.warn('⚠️ Failed to load frequent transactions:', error);
        // Не останавливаем загрузку всей страницы из-за этой ошибки
      }
      
      // Загружаем категории (системные + кастомные)
      console.log('🏷️ Fetching all categories...');
      try {
        const allCategoriesData = await apiClient.getAllCategories();
        console.log('🏷️ All categories data:', allCategoriesData);
        
        // Преобразуем системные категории в общий формат Category
        const systemCategories = (allCategoriesData.system_categories || []).map(cat => ({
          id: cat.db_name,
          name: cat.db_name,
          emoji: cat.emoji || null,
          is_system: true,
          type: 'both' as const // Системные категории могут быть и доходами, и расходами
        }));
        
        // Преобразуем пользовательские категории в общий формат Category
        const userCategories = (allCategoriesData.user_categories || []).map(catName => ({
          id: catName,
          name: catName,
          emoji: null,
          is_system: false,
          type: 'both' as const // Пользовательские категории могут быть и доходами, и расходами
        }));
        
        // Объединяем все категории (сначала системные, потом кастомные)
        const allCategories = [
          ...systemCategories.sort((a, b) => a.name.localeCompare(b.name)),
          ...userCategories.sort((a, b) => a.name.localeCompare(b.name))
        ];
        console.log('🏷️ Total categories:', allCategories.length, '(system:', systemCategories.length, ', custom:', userCategories.length, ')');
        console.log('🏷️ Categories sample:', allCategories.slice(0, 3));
        setCategories(allCategories);
      } catch (error: any) {
        console.error('❌ Failed to load categories:', error);
        console.error('❌ Error details:', error.message, error.stack);
        // Не останавливаем загрузку всей страницы из-за этой ошибки
      }
      
      console.log('✅ Home screen data loaded successfully');
    } catch (error: any) {
      console.error('❌ Error loading home data:', error);
      const errorMessage = error.message || 'Неизвестная ошибка';
      setDataError(errorMessage);
      console.error('Полная ошибка:', error);
    } finally {
      setLoading(false);
    }
  };


  // Обработчики для быстрых действий
  const handleQuickCategoryClick = (category: FrequentCategory) => {
    setSelectedQuickCategory(category);
    setShowQuickTransactionFlow(true);
    hapticFeedback.medium();
  };

  const handleQuickTransactionSave = async (title: string, amount: number, category: string, payment_method_id?: string) => {
    try {
      // Отправляем полную дату с временем в локальной таймзоне пользователя
      const transactionData: any = {
        type: 'expense' as const,
        amount,
        title,
        category,
        date: new Date().toISOString(),
      };

      // Добавляем счет, если он выбран
      if (payment_method_id) {
        transactionData.payment_method_id = payment_method_id;
      }

      await apiClient.addTransaction(transactionData);
      hapticFeedback.success();
      
      // Отслеживаем создание транзакции в Google Analytics
      trackAddTransaction(
        type,
        category || (type === 'income' ? 'Другое' : 'Разное'),
        amount,
        settings?.currency || 'RUB'
      );
      
      // Обновляем данные
      await loadData();
      
      setShowQuickTransactionFlow(false);
      setSelectedQuickCategory(null);
    } catch (error: any) {
      console.error('Failed to save quick transaction:', error);
      hapticFeedback.error();
      trackAnalyticsError('quick_transaction_failed', error.message || 'Unknown error');
      throw error;
    }
  };

  const handleAddTransaction = (type: 'income' | 'expense') => {
    hapticFeedback.medium();
    setTransactionType(type);
    setAmount('');
    setCategory('');
    setComment('');
    // Используем новый флоу вместо старой модалки
    setShowAddFlow(true);
  };

  const handleSubmitTransaction = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert(t('common.invalidNumber'));
      return;
    }

    try {
      setIsSubmitting(true);
      hapticFeedback.medium();

      // Отправляем полную дату с временем в ISO формате (с timezone)
      const transactionData = {
        type: transactionType,
        amount: parseFloat(amount),
        title: transactionType === 'income' ? t('transactions.incomes') : t('transactions.expenses'),
        category: category || (transactionType === 'income' ? t('homeScreen.defaultIncomeCategory') : t('homeScreen.defaultExpenseCategory')),
        comment: comment,
        date: new Date().toISOString(), // Полное время с timezone
      };

      console.log('📤 Submitting transaction:', transactionData);
      
      const response = await apiClient.addTransaction(transactionData);
      
      if (response.success) {
        console.log('✅ Transaction added successfully');
        hapticFeedback.success();
        
        // Отслеживаем создание транзакции в Google Analytics
        trackAddTransaction(
          transactionType,
          category || (transactionType === 'income' ? t('homeScreen.defaultIncomeCategory') : t('homeScreen.defaultExpenseCategory')),
          parseFloat(amount),
          settings?.currency || 'RUB'
        );
        
        // Закрываем модалку
        setShowAddModal(false);
        
        // Перезагружаем данные
        await loadData();
      } else {
        throw new Error('Failed to add transaction');
      }
    } catch (error: any) {
      console.error('❌ Error adding transaction:', error);
      hapticFeedback.error();
      trackAnalyticsError('add_transaction_failed', error.message || 'Unknown error');
      alert(`${t('common.error')}: ${error.message || t('homeScreen.addTransactionError')}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startVoiceRecording = async () => {
    // Проверка Premium подписки — показываем демо как в mobile
    if (!voicePremium.hasAccess) {
      setPremiumFeatureRequested('voice_input');
      setShowDemoVoiceModal(true);
      return;
    }
    
    try {
      console.log('🎤 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('✅ Microphone access granted');
      
      // Проверяем поддержку форматов
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          console.log('✅ Using MIME type:', mimeType);
          break;
        }
      }
      
      if (!selectedMimeType) {
        throw new Error('No supported audio format found');
      }
      
      const recorder = new MediaRecorder(stream, { mimeType: selectedMimeType });
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        console.log('📦 Audio chunk received, size:', e.data.size);
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        console.log('⏹️ Recording stopped, total chunks:', chunks.length);
        const audioBlob = new Blob(chunks, { type: selectedMimeType });
        console.log('🎵 Audio blob created, size:', audioBlob.size, 'bytes');
        
        if (audioBlob.size === 0) {
          console.error('❌ Audio blob is empty!');
          setTranscribedText(t('homeScreen.voiceAudioNotRecorded'));
          hapticFeedback.error();
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        try {
          setTranscribedText(t('homeScreen.voiceTranscribing'));
          console.log('📤 Sending audio to backend...');
          const response = await apiClient.transcribeVoice(audioBlob);
          console.log('📥 Transcription response:', response);
          
          if (response.success && response.text) {
            setTranscribedText(response.text);
            hapticFeedback.success();
            console.log('✅ Transcription successful:', response.text);
            
            // Обрабатываем транзакцию
            await handleVoiceTransaction(response.text);
          } else {
            setTranscribedText(t('homeScreen.voiceSpeechNotRecognized'));
            hapticFeedback.error();
            console.warn('⚠️ Transcription failed:', response);
          }
        } catch (error: any) {
          console.error('❌ Voice transcription error:', error);
          setTranscribedText(t('homeScreen.voiceGenericError', { message: error.message || t('common.error') }));
          hapticFeedback.error();
        }

        // Останавливаем все треки медиастрима
        stream.getTracks().forEach(track => track.stop());
        console.log('🔇 Microphone stream stopped');
      };

      recorder.onerror = (event: any) => {
        console.error('❌ MediaRecorder error:', event.error);
        alert(t('homeScreen.voiceRecordError', { message: event.error?.message || t('common.error') }));
      };

      recorder.onstart = () => {
        console.log('▶️ Recording started');
      };

      recorder.start();
      console.log('🎙️ MediaRecorder state:', recorder.state);
      setMediaRecorder(recorder);
      setIsRecording(true);
      setShowVoiceModal(true);
      setTranscribedText('');
      hapticFeedback.medium();
    } catch (error: any) {
      console.error('❌ Error accessing microphone:', error);
      alert(t('homeScreen.voiceMicAccessError', { message: error.message || t('common.error') }));
    }
  };

  const stopVoiceRecording = () => {
    console.log('🛑 Stopping recording...');
    if (mediaRecorder && isRecording) {
      console.log('📊 MediaRecorder state before stop:', mediaRecorder.state);
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
        console.log('✅ MediaRecorder.stop() called');
      } else {
        console.warn('⚠️ MediaRecorder is not in recording state:', mediaRecorder.state);
      }
      setIsRecording(false);
      hapticFeedback.light();
    } else {
      console.warn('⚠️ Cannot stop: mediaRecorder or isRecording is null/false');
    }
  };

  const closeVoiceModal = () => {
    if (isRecording) {
      stopVoiceRecording();
    }
    setShowVoiceModal(false);
    setTranscribedText('');
    setVoiceTransactionStatus('');
  };

  // Обработка голосовой транзакции с LLM парсингом
  const handleVoiceTransaction = async (text: string) => {
    console.log('🎙️ Processing voice transaction with LLM...');
    setVoiceTransactionStatus(t('homeScreen.voiceAnalyzing'));
    
    try {
      // Используем LLM для парсинга транзакции (поддержка массива)
      const parseResult = await apiClient.parseVoiceTransaction(text);
      const transactions = parseResult?.transactions || (parseResult?.transaction ? [parseResult.transaction] : []);
      
      if (!parseResult || !parseResult.success || transactions.length === 0) {
        setTranscribedText(`"${text}"`);
        setVoiceTransactionStatus(t('homeScreen.voiceParseFailed'));
        hapticFeedback.error();
        return;
      }
      
      // Хелпер: маппинг LLM → initialData для мастера
      const toInitialData = (parsed: any) => {
        let transactionDate: Date;
        if (parsed.transaction_date) {
          if (parsed.transaction_date.includes('T')) {
            // Дата с временем - используем как есть
            transactionDate = new Date(parsed.transaction_date);
          } else {
            // Дата без времени - создаем дату на указанный день с текущим временем
            // Используем локальную дату без timezone проблем
            const [year, month, day] = parsed.transaction_date.split('-').map(Number);
            const now = new Date();
            transactionDate = new Date(
              year, 
              month - 1, // Месяцы в JS начинаются с 0
              day,
              now.getHours(), 
              now.getMinutes(), 
              now.getSeconds(), 
              now.getMilliseconds()
            );
          }
        } else {
          transactionDate = new Date();
        }
        return {
          type: parsed.type as 'income' | 'expense',
          amount: parsed.amount ?? 0,
          title: parsed.item_name || '',
          category: parsed.category || (parsed.type === 'income' ? t('homeScreen.defaultIncomeCategory') : t('homeScreen.defaultExpenseCategory')),
          date: transactionDate,
          frequency: parsed.is_recurring && parsed.recurring_frequency ? parsed.recurring_frequency : 'onetime',
          payment_method_name: parsed.payment_method_name || undefined,
        };
      };

      const queue = transactions.map(toInitialData);
      setVoiceQueueResults(queue);
      setCurrentVoiceIndex(0);
      setProcessedVoiceCount(0);

      hapticFeedback.success();
      setVoiceTransactionStatus(t('homeScreen.voiceRecognizedCount', { count: queue.length }));

      // Показываем первую транзакцию
      setTimeout(() => {
        closeVoiceModal();
        setVoiceParsedData(queue[0]);
        setShowAddFlow(true);
      }, 500);
      
    } catch (error: any) {
      console.error('❌ Error processing voice transaction:', error);
      setVoiceTransactionStatus(t('homeScreen.voiceParseErrorPrefix', { message: error.message || t('common.error') }));
      hapticFeedback.error();
    }
  };

  const handleReceiptScan = () => {
    hapticFeedback.selection();
    
    // Проверка Premium подписки — показываем демо как в mobile
    if (!receiptPremium.hasAccess) {
      setPremiumFeatureRequested('receipt_scanner');
      setShowDemoReceiptModal(true);
      return;
    }
    
    setReceiptError(null);
    setShowReceiptModal(true);
  };

  // Обработчики Premium модалки
  const handlePremiumUpgrade = () => {
    setShowPremiumModal(false);
    setCurrentPage('subscription');
  };

  const handlePremiumModalClose = () => {
    console.log('🔴 Closing premium modal');
    setShowPremiumModal(false);
    setPremiumFeatureRequested(null);
  };

  // 🚀 НОВАЯ ВЕРСИЯ: Параллельная обработка всех чеков
  const handleReceiptFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    console.log(`📁 ${fileArray.length} file(s) selected`);

    // Валидация файлов
    const validFiles = fileArray.filter(file => {
      if (!file.type.startsWith('image/')) {
        console.warn(`⚠️ Skipping non-image file: ${file.name}`);
        return false;
      }
      if (file.size > 20 * 1024 * 1024) {
        console.warn(`⚠️ Skipping too large file: ${file.name}`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      alert(t('homeScreen.selectImagesPrompt'));
      return;
    }

    // Инициализация для параллельной обработки
    setReceiptQueue(validFiles);
    setTotalReceiptsToProcess(validFiles.length);
    setCurrentReceiptIndex(0);
    setProcessedReceipts(0);
    setScannedInBackground(0);
    setProcessedReceiptsResults([]);
    setReceiptError(null);
    setShowReceiptProgress(true);

    console.log(`🚀 Starting PARALLEL processing of ${validFiles.length} receipts`);

    // Запускаем параллельное сканирование всех чеков
    await processAllReceiptsInParallel(validFiles);
  };

  // 🔥 ПАРАЛЛЕЛЬНОЕ СКАНИРОВАНИЕ всех чеков одновременно
  const processAllReceiptsInParallel = async (files: File[]) => {
    const results: Array<{
      file: File;
      data: any;
      error?: string;
      index: number;
    }> = [];

    // Создаем промисы для всех чеков
    const scanPromises = files.map(async (file, index) => {
      try {
        console.log(`📸 [${index + 1}/${files.length}] Starting scan: ${file.name}`);
        
        const response = await apiClient.scanReceipt(file);
        
        if (!response || !response.success || !response.transaction) {
          const errorMsg = response?.error || t('homeScreen.receiptRecognizeError');
          console.error(`❌ [${index + 1}/${files.length}] Scan failed: ${errorMsg}`);
          
          return {
            file,
            data: null,
            error: errorMsg,
            index
          };
        }

        // Формируем данные для AddTransactionFlow
        const transaction = response.transaction;
        
        // Формируем дату и время из чека
        let receiptDate: Date;
        
        if (transaction.date) {
          if (transaction.date.includes('T')) {
            // Дата уже с временем в ISO формате
            receiptDate = new Date(transaction.date);
          } else {
            // Дата без времени - создаем локально без timezone проблем
            const [year, month, day] = transaction.date.split('-').map(Number);
            
            if (transaction.time) {
              // Есть время - парсим его
              const [hours, minutes] = transaction.time.split(':').map(Number);
              receiptDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
            } else {
              // Нет времени - используем полдень
              receiptDate = new Date(year, month - 1, day, 12, 0, 0, 0);
            }
          }
        } else {
          receiptDate = new Date();
        }
        
        const parsedData = {
          type: transaction.type as 'income' | 'expense',
          amount: parseFloat(transaction.amount) || 0,
          title: transaction.title || 'Покупка',
          category: transaction.category || 'Разное',
          date: receiptDate,
          frequency: 'onetime' as const,
          receipt_items: response.receipt_details?.items || [],
          currency: transaction.currency || settings?.currency_code || 'RUB',
        };

        console.log(`✅ [${index + 1}/${files.length}] Scan completed: ${file.name}`);
        
        // Обновляем счетчик отсканированных в фоне
        setScannedInBackground(prev => prev + 1);
        
        return {
          file,
          data: parsedData,
          error: undefined,
          index
        };

      } catch (error: any) {
        console.error(`❌ [${index + 1}/${files.length}] Error scanning:`, error);
        
        return {
          file,
          data: null,
          error: error?.message || t('homeScreen.receiptProcessError'),
          index
        };
      }
    });

    // ⚡ Запускаем ВСЕ промисы ПАРАЛЛЕЛЬНО
    console.log('⚡ Running all scans in PARALLEL...');
    const settledResults = await Promise.allSettled(scanPromises);
    
    // Собираем результаты
    settledResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        results.push(result.value);
      } else {
        results.push({
          file: files[index],
          data: null,
          error: t('homeScreen.receiptProcessError'),
          index
        });
      }
    });

    console.log(`✅ All ${files.length} receipts processed in parallel!`);
    console.log(`   Success: ${results.filter(r => !r.error).length}`);
    console.log(`   Errors: ${results.filter(r => r.error).length}`);

    // Сохраняем результаты
    setProcessedReceiptsResults(results);
    setShowReceiptProgress(false);

    // Показываем первое окно подтверждения
    showNextReceiptConfirmation(results, 0);
  };

  // 📋 Показать следующее окно подтверждения из очереди готовых результатов
  const showNextReceiptConfirmation = (results: typeof processedReceiptsResults, index: number) => {
    if (index >= results.length) {
      // Все чеки обработаны и подтверждены
      console.log('✅ All receipts confirmed!');
      setReceiptQueue([]);
      setCurrentReceiptIndex(0);
      setProcessedReceipts(0);
      setProcessedReceiptsResults([]);
      setTotalReceiptsToProcess(0);
      setScannedInBackground(0);
      setShowReceiptModal(false);
      setReceiptParsedData(null);
      hapticFeedback.success();
      return;
    }

    const result = results[index];
    setCurrentReceiptIndex(index);
    setReceiptImage(result.file);

    if (result.error) {
      // Чек не удалось распознать - показываем ошибку
      console.log(`⚠️ Showing error for receipt ${index + 1}/${results.length}`);
      setReceiptError(result.error);
      setReceiptParsedData(null);
      setShowReceiptModal(true);
      setShowAddFlow(false);
    } else {
      // Чек успешно распознан - показываем окно подтверждения
      console.log(`✅ Showing confirmation for receipt ${index + 1}/${results.length}`);
      setReceiptParsedData(result.data);
      setReceiptError(null);
      setShowReceiptModal(false);
      setShowAddFlow(true);
      hapticFeedback.success();
    }
  };

  const processReceipt = async (file: File) => {
    // Wrapper для обратной совместимости
    await handleReceiptFileChange({ target: { files: [file] } } as any);
  };

  const handleUpdateTransaction = async (transaction: any) => {
    try {
      setIsSubmitting(true);
      
      // Для Plaid транзакций используем специальный API
      if (transaction.source === 'plaid' && transaction.plaid_transaction_id) {
        console.log('💾 Updating Plaid transaction category:', {
          plaidTransactionId: transaction.plaid_transaction_id,
          category: transaction.category,
        });

        const response = await apiClient.updatePlaidTransactionCategory(
          transaction.plaid_transaction_id,
          transaction.category
        );

        if (response.success) {
          hapticFeedback.success();
          setShowTransactionDetails(false);
          setSelectedTransaction(null);
          await loadData(); // Обновляем список транзакций
        }
        return;
      }
      
      // Для обычных транзакций используем стандартный API
      // Извлекаем числовой ID из строкового ID транзакции
      const transactionId = parseInt(transaction.id.toString().replace(/\D/g, ''));
      
      console.log('💾 Updating transaction:', {
        id: transactionId,
        type: transaction.type,
        amount: transaction.amount,
        title: transaction.title,
        category: transaction.category,
        date: transaction.date
      });

      const response = await apiClient.updateTransaction(transactionId, {
        type: transaction.type,
        amount: transaction.amount,
        title: transaction.title,
        category: transaction.category,
        date: transaction.date
      });

      if (response.success) {
        hapticFeedback.success();
        
        // Отслеживаем редактирование транзакции в Google Analytics
        trackEditTransaction(transaction.type);
        
        setShowTransactionDetails(false);
        setSelectedTransaction(null);
        await loadData(); // Обновляем список транзакций
      }
    } catch (error: any) {
      console.error('Error updating transaction:', error);
      hapticFeedback.error();
      trackAnalyticsError('update_transaction_failed', error.message || 'Unknown error');
      alert(t('homeScreen.updateTransactionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      // Не удаляем транзакции из Plaid
      if (selectedTransaction?.source === 'plaid') {
        console.log('⚠️ Cannot delete Plaid transactions');
      alert(t('homeScreen.deleteBankTransaction'));
        return;
      }
      
      setIsSubmitting(true);
      
      // Извлекаем числовой ID из строкового ID транзакции
      const transactionId = parseInt(selectedTransaction.id.toString().replace(/\D/g, ''));
      
      console.log('🗑️ Deleting transaction:', {
        id: transactionId,
        type: selectedTransaction.type
      });

      const response = await apiClient.deleteTransaction(transactionId, selectedTransaction.type);

      if (response.success) {
        hapticFeedback.success();
        
        // Отслеживаем удаление транзакции в Google Analytics
        trackDeleteTransaction(selectedTransaction.type);
        
        setShowTransactionDetails(false);
        setSelectedTransaction(null);
        await loadData(); // Обновляем список транзакций
      }
    } catch (error: any) {
      console.error('Error deleting transaction:', error);
      hapticFeedback.error();
      trackAnalyticsError('delete_transaction_failed', error.message || 'Unknown error');
      alert(t('homeScreen.deleteTransactionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home-screen page">
      {!isLoadingCritical && (
        <Header
          title={t(`greetings.${getGreeting()}`)}
        />
      )}

      {/* Preloader - показываем сразу при загрузке критичных данных */}
      {isLoadingCritical && (
        <Preloader message={t('home.loadingData')} />
      )}

      {/* Error State */}
      {dataError && !isLoadingCritical && (
        <div className="section">
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '20px',
            }}>
              <div style={{ marginBottom: '16px' }}>
                <Icon name="alert-circle" size={48} color="#FF3B30" />
              </div>
              <h3 style={{ marginBottom: '12px', color: '#FF3B30' }}>{t('common.error')}</h3>
              <p style={{ color: '#86868B', marginBottom: '20px' }}>{dataError}</p>
              <Button variant="primary" onClick={loadCriticalData}>
                {t('common.retry')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content - показываем сразу после загрузки критичных данных */}
      {!isLoadingCritical && !dataError && (
      <>
      {/* HomeTabs — home, analytics, recurring, categories, limits */}
      <HomeTabs activeTab={homeTab} onTabChange={setHomeTab} />

      {/* Контент по табу */}
      {homeTab === 'analytics' && <AnalyticsScreen />}
      {homeTab === 'recurring' && <RecurringTransactionsScreen isEmbedded />}
      {homeTab === 'categories' && <CategoriesContent showHeader={false} />}
      {homeTab === 'limits' && <LimitsScreen isEmbedded />}

      {homeTab === 'home' && (
      <>
      {/* SummaryCards — компактный виджет баланса (как CompactBalanceWidget в mobile) */}
      <div className="section">
        <SummaryCards
          income={summary.total_income}
          expense={summary.total_expense}
          balance={summary.balance}
          currency={settings?.currency || 'RUB'}
          language={settings?.language || 'ru'}
        />
      </div>

      {/* QuickActions — Add, Voice, Receipt, частые категории (как mobile) */}
      <div className="section">
        <QuickActions
          categories={frequentCategories}
          onCategoryClick={handleQuickCategoryClick}
          onQuickAdd={(type) => {
            setQuickAddType(type);
            setShowQuickAdd(true);
          }}
          onVoicePress={startVoiceRecording}
          onReceiptPress={handleReceiptScan}
          isPremium={!!(voicePremium && receiptPremium)}
          isLoading={isLoadingFrequent}
        />
      </div>

      {/* InteractivePieChart — расходы по категориям (как mobile) */}
      <div className="section">
        <InteractivePieChart
          expenses={categoryExpenses}
          currency={settings?.currency || 'RUB'}
          isLoading={isLoadingPieChart}
          grandTotal={summary.total_expense}
        />
      </div>

      </>
      )}

      </>
      )}

      {/* Simple Add Transaction Modal */}
      {showAddModal && (
        <div 
          className="modal-overlay modal-overlay--center"
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="modal-content"
            style={{ 
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px', 
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ 
              marginBottom: '24px', 
              textAlign: 'center',
              fontSize: '22px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <Icon name={transactionType === 'income' ? 'trending-up' : 'trending-down'} size={24} />
              {transactionType === 'income' ? t('homeScreen.addIncomeTitle') : t('homeScreen.addExpenseTitle')}
            </h3>
            
            <div>
              {/* Amount Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1D1D1F'
                }}>
                  {t('homeScreen.amountLabel')}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '18px',
                    fontWeight: '600',
                    border: '2px solid #F2F2F7',
                    borderRadius: '12px',
                    backgroundColor: '#F2F2F7',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => e.target.style.borderColor = transactionType === 'income' ? '#34C759' : '#FF3B30'}
                  onBlur={(e) => e.target.style.borderColor = '#F2F2F7'}
                />
              </div>

              {/* Category Select */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1D1D1F'
                }}>
                  {t('homeScreen.categoryOptionalLabel')}
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    border: '2px solid #F2F2F7',
                    borderRadius: '12px',
                    backgroundColor: '#F2F2F7',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%231D1D1F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 16px center',
                    paddingRight: '40px',
                  }}
                  onFocus={(e) => e.target.style.borderColor = transactionType === 'income' ? '#34C759' : '#FF3B30'}
                  onBlur={(e) => e.target.style.borderColor = '#F2F2F7'}
                >
                  <option value="">
                    {transactionType === 'income' ? t('homeScreen.selectIncomeCategory') : t('homeScreen.selectExpenseCategory')}
                  </option>
                  
                  {/* Системные категории */}
                  {categories.filter(cat => cat.is_system).length > 0 && (
                    <optgroup label={t('homeScreen.systemCategories')}>
                      {categories
                        .filter(cat => cat.is_system)
                        .map(cat => (
                          <option key={cat.id} value={cat.name}>
                            {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                          </option>
                        ))
                      }
                    </optgroup>
                  )}
                  
                  {/* Пользовательские категории */}
                  {categories.filter(cat => !cat.is_system).length > 0 && (
                    <optgroup label={t('homeScreen.userCategories')}>
                      {categories
                        .filter(cat => !cat.is_system)
                        .map(cat => (
                          <option key={cat.id} value={cat.name}>
                            {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                          </option>
                        ))
                      }
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Comment Input */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1D1D1F'
                }}>
                  {t('homeScreen.commentOptionalLabel')}
                </label>
                <textarea
                  placeholder={t('homeScreen.commentPlaceholder')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    border: '2px solid #F2F2F7',
                    borderRadius: '12px',
                    backgroundColor: '#F2F2F7',
                    outline: 'none',
                    resize: 'none',
                    fontFamily: 'inherit',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => e.target.style.borderColor = transactionType === 'income' ? '#34C759' : '#FF3B30'}
                  onBlur={(e) => e.target.style.borderColor = '#F2F2F7'}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button 
                  variant="secondary" 
                  onClick={() => setShowAddModal(false)}
                  style={{ flex: 1 }}
                  disabled={isSubmitting}
                >
                  Отмена
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSubmitTransaction}
                  style={{ 
                    flex: 1,
                    backgroundColor: transactionType === 'income' ? '#34C759' : '#FF3B30',
                  }}
                  disabled={isSubmitting || !amount}
                >
                  {isSubmitting ? t('homeScreen.addProcessing') : t('common.add')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Voice Recording Modal */}
      {showVoiceModal && (
        <div 
          className="modal-overlay modal-overlay--center"
          onClick={closeVoiceModal}
        >
          <div
            className="modal-content modal-content--center modal-content--voice"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-content__title">
              {isRecording ? t('homeScreen.speakNow') : t('homeScreen.resultLabel')}
            </h3>
            
            {isRecording ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                {/* Recording Animation */}
                <div style={{
                  width: '100px',
                  height: '100px',
                  margin: '0 auto 24px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  boxShadow: '0 8px 32px rgba(255, 107, 107, 0.4)'
                }}>
                  <Icon name="mic" size={48} color="white" />
                </div>
                
                <p style={{ color: '#86868B', marginBottom: '24px', fontSize: '15px' }}>
                  {t('homeScreen.pressToFinish')}
                </p>
                
                <button 
                  className="neo-raised"
                  onClick={stopVoiceRecording}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'var(--neo-bg)',
                    border: 'none',
                    borderRadius: '14px',
                    color: 'var(--color-danger)',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t('homeScreen.stopRecording')}
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ marginBottom: '16px' }}>
                  {transcribedText === 'Распознаю голос...' ? (
                    <Icon name="hourglass" size={48} color="#FF9500" />
                  ) : transcribedText.includes('Ошибка') ? (
                    <Icon name="x-circle" size={48} color="#FF3B30" />
                  ) : (
                    <Icon name="check-circle" size={48} color="#34C759" />
                  )}
                </div>
                
                <p style={{ 
                  color: '#1D1D1F', 
                  marginBottom: '12px', 
                  fontSize: '16px',
                  fontWeight: '500',
                  lineHeight: '1.5'
                }}>
                  {transcribedText}
                </p>

                {voiceTransactionStatus && (
                  <p style={{ 
                    color: voiceTransactionStatus.includes('✅') ? '#34C759' : voiceTransactionStatus.includes('❌') ? '#FF3B30' : '#86868B',
                    fontSize: '15px', 
                    marginBottom: '24px',
                    fontWeight: '600'
                  }}>
                    {voiceTransactionStatus}
                  </p>
                )}
                
                <Button 
                  variant="primary" 
                  onClick={closeVoiceModal}
                  style={{ width: '100%' }}
                >
                  Закрыть
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
      `}</style>

      {/* FAB */}
      <button className="fab" onClick={() => {
        hapticFeedback.medium();
        setShowTransactionTypeModal(true);
      }}>
        <Icon name="add" size={28} color="white" />
      </button>

      {/* Transaction Type Selection Modal */}
      {showTransactionTypeModal && createPortal(
        <div
          className="modal-overlay"
          onClick={() => setShowTransactionTypeModal(false)}
        >
          <div
            className="modal-content modal-content--transaction-type"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-content__title">
              {t('home.chooseTransactionType')}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Доход */}
              <button
                className="neo-raised"
                onClick={() => {
                  hapticFeedback.selection();
                  setTransactionType('income');
                  setShowTransactionTypeModal(false);
                  setShowAddFlow(true);
                }}
                style={{
                  width: '100%',
                  padding: '20px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  borderRadius: '16px',
                  color: 'var(--color-success)',
                  fontSize: '18px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon name="trending-up" size={24} color="var(--color-success)" />
                <span>{t('home.addIncome')}</span>
              </button>

              {/* Расход */}
              <button
                className="neo-raised"
                onClick={() => {
                  hapticFeedback.selection();
                  setTransactionType('expense');
                  setShowTransactionTypeModal(false);
                  setShowAddFlow(true);
                }}
                style={{
                  width: '100%',
                  padding: '20px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  borderRadius: '16px',
                  color: 'var(--color-danger)',
                  fontSize: '18px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon name="trending-down" size={24} color="var(--color-danger)" />
                <span>{t('home.addExpense')}</span>
              </button>

              {/* Кнопка отмены */}
              <button
                onClick={() => {
                  hapticFeedback.light();
                  setShowTransactionTypeModal(false);
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#8E8E93',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Transaction Flow */}
      {showAddFlow && (() => {
        const isPremium = settings?.subscription_status === 'premium' || settings?.subscription_status === 'lifetime_premium';
        const initialData = voiceParsedData || receiptParsedData;
        const isReceiptQueue = processedReceiptsResults.length > 0;
        const isVoiceQueue = voiceQueueResults.length > 0;
        
        console.log('🎯 Opening AddTransactionFlow with:');
        console.log('  - isPremium:', isPremium);
        console.log('  - initialData:', initialData);
        console.log('  - isReceiptQueue:', isReceiptQueue);
        console.log('  - isVoiceQueue:', isVoiceQueue);
        console.log('  - processedReceiptsResults length:', processedReceiptsResults.length);
        console.log('  - currentReceiptIndex:', currentReceiptIndex);
        console.log('  - voiceQueueResults length:', voiceQueueResults.length);
        console.log('  - currentVoiceIndex:', currentVoiceIndex);
        
        return (
          <AddTransactionFlow
            onClose={() => {
              setShowAddFlow(false);
              setVoiceParsedData(null);
              setReceiptParsedData(null);
              
              // Очищаем очередь голосовых транзакций при закрытии
              if (isVoiceQueue) {
                setVoiceQueueResults([]);
                setCurrentVoiceIndex(0);
                setProcessedVoiceCount(0);
              }
              
              // Если есть еще чеки в очереди - показываем модальное окно снова
              if (isReceiptQueue) {
                setShowReceiptModal(true);
              }
            }}
            categories={categories}
            initialData={initialData || undefined}
            showSkip={isReceiptQueue && processedReceiptsResults.length > 1}
            queueProgress={
              isVoiceQueue 
                ? { current: currentVoiceIndex + 1, total: voiceQueueResults.length }
                : isReceiptQueue 
                  ? { current: currentReceiptIndex + 1, total: processedReceiptsResults.length }
                  : undefined
            }
            onSkip={async () => {
              // Пропускаем текущий чек и переходим к следующему
              console.log(`⏭️ Skipping receipt ${currentReceiptIndex + 1}/${processedReceiptsResults.length}`);
              hapticFeedback.selection();
              
              const nextIndex = currentReceiptIndex + 1;
              setProcessedReceipts(nextIndex);
              setShowAddFlow(false);
              setReceiptParsedData(null);
              
              if (nextIndex < processedReceiptsResults.length) {
                // Есть еще чеки - показываем следующее окно подтверждения
                console.log(`📋 Showing next receipt confirmation: ${nextIndex + 1}/${processedReceiptsResults.length}`);
                showNextReceiptConfirmation(processedReceiptsResults, nextIndex);
              } else {
                // Все чеки обработаны
                console.log('✅ All receipts processed (some skipped)');
                setReceiptQueue([]);
                setCurrentReceiptIndex(0);
                setProcessedReceipts(0);
                setProcessedReceiptsResults([]);
                setTotalReceiptsToProcess(0);
                setScannedInBackground(0);
                await loadData();
              }
            }}
            onSubmit={async (data: TransactionData) => {
              try {
                console.log('📝 Creating transaction with data:', data);
                
                // Проверяем, нужно ли создать новую категорию (только для расходов)
                if (data.type === 'expense' && data.category) {
                  const categoryExists = categories.some(cat => cat.name === data.category);
                  if (!categoryExists) {
                    console.log('📁 Creating new category:', data.category);
                    try {
                      await apiClient.addCategory(data.category);
                      // Обновляем список категорий
                      const updatedCategories = await apiClient.getCategories();
                      setCategories(updatedCategories.categories || []);
                      console.log('✅ New category created successfully');
                    } catch (categoryError) {
                      console.error('⚠️ Failed to create category, but continuing with transaction:', categoryError);
                      // Продолжаем создание транзакции даже если категория не создалась
                    }
                  }
                }
                
                // Проверяем, является ли транзакция регулярной
                const isRecurring = data.frequency && data.frequency !== 'onetime';
                
                if (isRecurring) {
                  // Создаем регулярную транзакцию
                  if (data.type === 'income') {
                    // Регулярный доход
                    // Форматируем дату БЕЗ timezone conversion
                    const formatDateLocal = (date: Date): string => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      return `${year}-${month}-${day}`;
                    };
                    
                    const recurringIncomeData = {
                      title: data.title,
                      default_amount: data.amount,
                      frequency: data.frequency as 'daily' | 'weekly' | 'monthly' | 'yearly',
                      start_date: data.date ? formatDateLocal(data.date) : formatDateLocal(new Date()),
                      notification_time: data.notificationTime || '09:00',
                      reminder_times: data.reminderTimes || [],
                      reminder_offsets: data.reminderOffsets || [0],
                      reminders_enabled: data.remindersEnabled || false,
                      description: data.description || '',
                    };
                    
                    console.log('💰 Creating recurring income:', recurringIncomeData);
                    await apiClient.addRecurringIncomeDefinition(recurringIncomeData);
                  } else {
                    // Регулярный расход
                    // Проверяем поддерживаемые типы (daily не поддерживается для расходов)
                    if (data.frequency === 'daily') {
                      throw new Error('Ежедневные регулярные расходы не поддерживаются. Используйте еженедельные.');
                    }
                    
                    // Форматируем дату БЕЗ timezone conversion
                    const formatDateLocal = (date: Date): string => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      return `${year}-${month}-${day}`;
                    };
                    
                    const recurringExpenseData = {
                      title: data.title,
                      amount: data.amount,
                      category: data.category || 'Разное',
                      expense_type: data.frequency as 'weekly' | 'monthly' | 'yearly',
                      start_date_recurring: data.date ? formatDateLocal(data.date) : formatDateLocal(new Date()),
                      notification_time: data.notificationTime || '09:00',
                      disable_payment_notifications: !data.remindersEnabled, // Инвертируем для совместимости
                      payment_method_id: data.payment_method_id ? parseInt(data.payment_method_id) : undefined,
                      reminder_times: data.reminderTimes || [],
                      reminder_offsets: data.reminderOffsets || [0],
                      reminders_enabled: data.remindersEnabled || false,
                      description: data.description || '',
                    };
                    
                    console.log('💸 Creating recurring expense:', recurringExpenseData);
                    await apiClient.addRecurringExpense(recurringExpenseData);
                  }
                  
                  console.log('✅ Recurring transaction created successfully');
                } else {
                  // Создаем обычную одноразовую транзакцию
                  
                  // Формируем комментарий со списком товаров (если есть)
                  let comment = '';
                  if (data.receipt_items && data.receipt_items.length > 0) {
                    const itemsList = data.receipt_items
                      .map(item => {
                        let itemStr = `• ${item.name}`;
                        if (item.quantity && item.unit_price) {
                          itemStr += ` (${item.quantity} шт. × ${item.unit_price})`;
                        }
                        itemStr += ` = ${item.amount}`;
                        return itemStr;
                      })
                      .join('\n');
                    comment = `📋 Список покупок:\n${itemsList}`;
                    console.log('💾 Saving receipt items to comment:', comment);
                  }
                  
                  // Отправляем полную дату с временем в ISO формате (с timezone)
                  const transactionData = {
                    type: data.type,
                    amount: data.amount,
                    title: data.title,
                    category: data.category || (data.type === 'income' ? 'Другое' : 'Разное'),
                    comment: comment,
                    description: data.description || '',
                    date: data.date ? data.date.toISOString() : new Date().toISOString(),
                    payment_method_id: data.payment_method_id,
                    source_type: data.source_type,
                    source_goal_id: data.source_goal_id,
                  };

                  console.log('💳 Creating one-time transaction:', transactionData);
                  const response = await apiClient.addTransaction(transactionData);
                  
                  if (!response.success) {
                    throw new Error('Failed to create transaction');
                  }
                  
                  // Отслеживаем создание транзакции в Google Analytics
                  trackAddTransaction(
                    data.type,
                    data.category || (data.type === 'income' ? 'Другое' : 'Разное'),
                    data.amount,
                    settings?.currency || 'RUB'
                  );
                }
                
                hapticFeedback.success();
                setVoiceParsedData(null);
                setReceiptParsedData(null);
                
                // Увеличиваем счетчик обработанных чеков
                if (isReceiptQueue) {
                  const nextIndex = currentReceiptIndex + 1;
                  setProcessedReceipts(nextIndex);
                  
                  if (nextIndex < processedReceiptsResults.length) {
                    // Есть еще чеки - показываем следующее окно подтверждения
                    console.log(`📋 Showing next receipt confirmation: ${nextIndex + 1}/${processedReceiptsResults.length}`);
                    setShowAddFlow(false);
                    await loadData();
                    
                    // Показываем следующее окно подтверждения (ВСЕ чеки уже отсканированы!)
                    showNextReceiptConfirmation(processedReceiptsResults, nextIndex);
                  } else {
                    // Все чеки обработаны и подтверждены
                    console.log('✅ All receipts confirmed!');
                    setShowAddFlow(false);
                    setReceiptQueue([]);
                    setCurrentReceiptIndex(0);
                    setProcessedReceipts(0);
                    setProcessedReceiptsResults([]);
                    setTotalReceiptsToProcess(0);
                    setScannedInBackground(0);
                    await loadData();
                  }
                } else {
                  // Обычное добавление транзакции (не из чека)
                  
                  // Проверяем, есть ли очередь голосовых транзакций
                  const isVoiceQueue = voiceQueueResults.length > 0;
                  
                  if (isVoiceQueue) {
                    const nextIndex = currentVoiceIndex + 1;
                    setProcessedVoiceCount(nextIndex);
                    
                    if (nextIndex < voiceQueueResults.length) {
                      // Есть еще транзакции - показываем следующую
                      console.log(`🎙️ Showing next voice transaction: ${nextIndex + 1}/${voiceQueueResults.length}`);
                      setShowAddFlow(false);
                      await loadData();
                      
                      // Показываем следующую транзакцию из очереди
                      setTimeout(() => {
                        setCurrentVoiceIndex(nextIndex);
                        setVoiceParsedData(voiceQueueResults[nextIndex]);
                        setShowAddFlow(true);
                      }, 300);
                    } else {
                      // Все голосовые транзакции обработаны
                      console.log('✅ All voice transactions confirmed!');
                      setShowAddFlow(false);
                      setVoiceQueueResults([]);
                      setCurrentVoiceIndex(0);
                      setProcessedVoiceCount(0);
                      setVoiceParsedData(null);
                      await loadData();
                    }
                  } else {
                    // Обычное добавление (не из чека и не из голосового)
                    setShowAddFlow(false);
                    await loadData();
                  }
                }
              } catch (error: any) {
                console.error('Error adding transaction:', error);
                hapticFeedback.error();
                
                // Обработка ошибки дублирования названия
                if (error?.response?.status === 409 && error?.response?.data?.code === 'DUPLICATE_TITLE') {
                  throw new Error(error.response.data.error);
                }
                
                throw error;
              }
            }}
            isPremium={isPremium}
            currency={settings?.currency || 'RUB'}
            currencyCode={settings?.currency || 'RUB'}
            initialType={transactionType || undefined}
          />
        );
      })()}

      {/* Receipt Scanner Modal */}
      {showReceiptModal && (
        <div
          className="modal-overlay modal-overlay--center"
          onClick={() => !isScanning && setShowReceiptModal(false)}
        >
          <div
            className="modal-content modal-content--center modal-content--receipt"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-content__title">
              📸 {t('home.scanReceiptTitle')}
            </h3>
            
            {/* Индикатор прогресса для bulk upload */}
            {receiptQueue.length > 1 && (
              <div style={{
                background: '#F2F2F7',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#667eea',
                  marginBottom: '6px'
                }}>
                  📋 Обработано {processedReceipts} из {receiptQueue.length} чеков
                </div>
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: '#E5E5EA',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(processedReceipts / receiptQueue.length) * 100}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                {isScanning && (
                  <div style={{
                    fontSize: '12px',
                    color: '#86868B',
                    marginTop: '6px'
                  }}>
                    Обработка чека {currentReceiptIndex + 1}...
                  </div>
                )}
              </div>
            )}
            
            {isScanning ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{
                  display: 'inline-block',
                  width: '50px',
                  height: '50px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #FF6B6B',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}>
                </div>
                <p style={{ marginTop: '20px', color: '#666' }}>{t('home.scanReceiptProcessing')}</p>
              </div>
            ) : receiptError ? (
              <>
                <div style={{ 
                  backgroundColor: '#FFF3F3', 
                  borderRadius: '12px', 
                  padding: '20px',
                  marginBottom: '24px',
                  border: '1px solid #FFE0E0'
                }}>
                  <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                    <Icon name="alert-triangle" size={48} color="#FF3B30" />
                  </div>
                  <p style={{ 
                    color: '#FF3B30', 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    textAlign: 'center',
                    marginBottom: '8px'
                  }}>
                    {receiptError}
                  </p>
                  <p style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>
                    Пожалуйста, загрузите фото настоящего чека
                  </p>
                </div>
                
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handleReceiptFileChange}
                  style={{ display: 'none' }}
                  id="receipt-file-input-retry"
                />
                
                <label
                  htmlFor="receipt-file-input-retry"
                  className="neo-raised"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: 'var(--neo-bg)',
                    color: '#FF6B6B',
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '16px',
                    fontWeight: '700',
                    textAlign: 'center',
                    cursor: 'pointer',
                    marginBottom: '12px'
                  }}
                >
                  <Icon name="refresh" size={20} color="#FF6B6B" />
                  Попробовать снова
                </label>
                
                <button
                  className="neo-raised"
                  onClick={() => {
                    setReceiptError(null);
                    setShowReceiptModal(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'var(--neo-bg)',
                    border: 'none',
                    borderRadius: '14px',
                    color: '#8E8E93',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <>
                <p style={{ color: '#666', marginBottom: '24px', textAlign: 'center' }}>
                  {t('home.scanReceiptDesc')}
                </p>
                
                <div style={{
                  background: '#F0F9FF',
                  border: '1px solid #BAE6FD',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center'
                }}>
                  <Icon name="lightbulb" size={18} color="#0369A1" />
                  <div style={{ fontSize: '14px', color: '#0369A1', fontWeight: '500' }}>
                    Можно загрузить сразу несколько чеков!
                  </div>
                  <div style={{ fontSize: '12px', color: '#0C4A6E', marginTop: '4px' }}>
                    Мы обработаем их по очереди
                  </div>
                </div>
                
                {/* Кнопка открытия камеры */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handleReceiptFileChange}
                  style={{ display: 'none' }}
                  id="receipt-file-input-camera"
                />
                
                <label
                  htmlFor="receipt-file-input-camera"
                  className="neo-raised"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: 'var(--neo-bg)',
                    color: '#FF6B6B',
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '16px',
                    fontWeight: '700',
                    textAlign: 'center',
                    cursor: 'pointer',
                    marginBottom: '12px'
                  }}
                >
                  <Icon name="camera" size={20} color="#FF6B6B" />
                  <span>Сфотографировать чек(и)</span>
                </label>

                {/* Кнопка загрузки из галереи */}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleReceiptFileChange}
                  style={{ display: 'none' }}
                  id="receipt-file-input-gallery"
                />
                
                <label
                  htmlFor="receipt-file-input-gallery"
                  className="neo-raised"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: 'var(--neo-bg)',
                    color: '#FF6B6B',
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '16px',
                    fontWeight: '700',
                    textAlign: 'center',
                    cursor: 'pointer',
                    marginBottom: '12px'
                  }}
                >
                  <Icon name="folder" size={20} color="#FF6B6B" />
                  <span>Загрузить чек(и)</span>
                </label>
                
                <button
                  className="neo-raised"
                  onClick={() => setShowReceiptModal(false)}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'var(--neo-bg)',
                    border: 'none',
                    borderRadius: '14px',
                    color: '#8E8E93',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel')}
                </button>
              </>
            )}
            
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}


      {/* Receipt Processing Progress Modal - ПАРАЛЛЕЛЬНАЯ ОБРАБОТКА */}
      {showReceiptProgress && (
        <div
          className="modal-overlay modal-overlay--center"
          style={{ 
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }}
        >
          <div
            className="modal-content modal-content--center modal-content--parallel"
            style={{
              padding: '40px 32px',
              maxWidth: '400px',
              textAlign: 'center'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <Icon name="zap" size={48} color="#667eea" />
            </div>
            
            <h3 style={{ 
              fontSize: '20px', 
              fontWeight: '700',
              color: '#1D1D1F',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center'
            }}>
              <Icon name="zap" size={24} color="#667eea" />
              Параллельная обработка
            </h3>
            
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                Сканируем {totalReceiptsToProcess} {totalReceiptsToProcess === 1 ? 'чек' : totalReceiptsToProcess < 5 ? 'чека' : 'чеков'} одновременно!
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                Отсканировано: {scannedInBackground} из {totalReceiptsToProcess}
              </div>
            </div>
            
            {/* Прогресс-бар */}
            <div style={{
              width: '100%',
              height: '10px',
              background: '#E5E5EA',
              borderRadius: '5px',
              overflow: 'hidden',
              marginBottom: '16px'
            }}>
              <div style={{
                width: `${(scannedInBackground / totalReceiptsToProcess) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%)',
                transition: 'width 0.3s ease'
              }} />
            </div>
            
            {/* Спиннер */}
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            
            <p style={{ 
              marginTop: '20px',
              color: '#667eea',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Все чеки обрабатываются параллельно...
            </p>

            <div style={{
              marginTop: '20px',
                  padding: '12px',
                  background: '#F5F5F7',
                  borderRadius: '10px',
                  fontSize: '13px',
                  color: '#86868B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center'
                }}>
                  <Icon name="lightbulb" size={16} color="#86868B" />
                  <span>Окна подтверждения появятся по мере готовности</span>
                </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showTransactionDetails && selectedTransaction && (
        <TransactionEditModal
          transaction={selectedTransaction}
          isSubmitting={isSubmitting}
          onUpdate={handleUpdateTransaction}
          onDelete={handleDeleteTransaction}
          onClose={() => {
            setShowTransactionDetails(false);
            const wasPlaidTransaction = selectedTransaction?.source === 'plaid';
            setSelectedTransaction(null);
            // Перезагружаем данные, если это была транзакция из Plaid
            if (wasPlaidTransaction) {
              loadData();
            }
          }}
        />
      )}

      {/* Quick Transaction Flow */}
      {showQuickTransactionFlow && selectedQuickCategory && (
        <QuickTransactionFlow
          category={selectedQuickCategory}
          onClose={() => {
            setShowQuickTransactionFlow(false);
            setSelectedQuickCategory(null);
          }}
          onSave={handleQuickTransactionSave}
        />
      )}

      {/* Quick Add Transaction Modal */}
      {showQuickAdd && (
        <QuickAddTransaction
          type={quickAddType}
          currency={settings?.currency || 'RUB'}
          onClose={() => setShowQuickAdd(false)}
          onSuccess={() => {
            loadData();
            setShowQuickAdd(false);
          }}
        />
      )}

      {/* Demo модалки для непремиум (как в mobile) */}
      <DemoVoiceModal
        visible={showDemoVoiceModal}
        onClose={() => {
          setShowDemoVoiceModal(false);
          setPremiumFeatureRequested(null);
        }}
        onRequestPremium={() => {
          setShowDemoVoiceModal(false);
          setPremiumFeatureRequested('voice_input');
          setShowPremiumModal(true);
        }}
      />
      <DemoReceiptModal
        visible={showDemoReceiptModal}
        onClose={() => {
          setShowDemoReceiptModal(false);
          setPremiumFeatureRequested(null);
        }}
        onRequestPremium={() => {
          setShowDemoReceiptModal(false);
          setPremiumFeatureRequested('receipt_scanner');
          setShowPremiumModal(true);
        }}
      />

      {/* Premium Upgrade Modal */}
      {premiumFeatureRequested && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={handlePremiumModalClose}
          featureId={premiumFeatureRequested}
          onUpgrade={handlePremiumUpgrade}
        />
      )}

    </div>
  );
};
